import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { Me } from 'service/user'
import { FormValues } from 'utils/type'
import { ReactComponent as StarIcon } from 'svg/star_white.svg'
import classNames from 'classnames'
import { AiThumbnailTitle, LinkDetailType, ThumbnailType } from 'service/list'
import { Modal } from 'components/layout/Modal'
import { PlanTag } from 'components/planTag'

import { removeLocalStorage, saveLocalStorage } from 'utils/common'
import { AiMethodThumbnail } from 'components/thumbnail/AiMethodThumbnail'
import { DirectMethodThumbnail } from 'components/thumbnail/DirectMethodThumbnail'
import useCreateThumbnailByPlacid from 'hooks/query/useCreateThumbnailByPlacid'
import useGetThumbnailByPlacid from 'hooks/query/useGetThumbnailByPlacid'
import { SelectionModal } from 'components/layout/SelectionModal'

interface ThumbnailDisplayProps {
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  control: Control<FormValues, any>
  userInfo: Me
  thumbnailList: ThumbnailType[]
  thumbnailPlacIdList: ThumbnailType[]
  translatePlanName: (planName: string) => string
  error: any
  setError: Dispatch<SetStateAction<any>>
  isOpenLinkMethod: boolean
  selectedLinks: LinkDetailType[]
  aiRecommendThumbnailTitle: AiThumbnailTitle | null
  placIdThumbnail: { id: number; path: string }[]
  setPlacIdThumbnail: Dispatch<SetStateAction<{ id: number; path: string }[]>>
  isSaveThumbnail: boolean
  setIsSaveThumbnail: Dispatch<SetStateAction<boolean>>
  thumbNailDivRef: React.MutableRefObject<HTMLDivElement | null>
  selectedPlacIdThumbnail: { id: number; path: string } | null
  setSelectedPlacIdThumbnail: Dispatch<
    SetStateAction<{ id: number; path: string } | null>
  >
  selectedItem: ThumbnailType | null
  setSelectedItem: Dispatch<SetStateAction<ThumbnailType | null>>
}
export const ThumbnailDisplay = ({
  watch,
  setValue,
  control,
  userInfo,
  thumbnailList,
  thumbnailPlacIdList,
  translatePlanName,
  error,
  setError,
  isOpenLinkMethod,
  selectedLinks,
  aiRecommendThumbnailTitle,
  placIdThumbnail,
  setPlacIdThumbnail,
  isSaveThumbnail,
  setIsSaveThumbnail,
  thumbNailDivRef,
  selectedPlacIdThumbnail,
  setSelectedPlacIdThumbnail,
  selectedItem,
  setSelectedItem,
}: PropsWithChildren<ThumbnailDisplayProps>) => {
  const [isAiName, setAiName] = useState<boolean>(true)
  const [thumbnailMethod, setThumbnailMethod] = useState<'AI' | 'DIRECT'>('AI')
  const [isOpenModal, setOpenModal] = useState<boolean>(false)
  const [isOpen, setOpen] = useState<boolean>(false)
  const [isRememberChannel, setIsRememberChannel] = useState<boolean>(false)

  const [selectedImgSrc, setSelectedImgSrc] = useState<string | null>(null)

  const [isThumbnailDailyLimit, setIsThumbnailDailyLimit] =
    useState<boolean>(false)

  const placIdThumbnailRef = useRef<HTMLDivElement>(null)
  const { mutate, isPending: createThumbnailPending } =
    useCreateThumbnailByPlacid()

  const { mutate: imgMutate, isPending: imgIsPending } =
    useGetThumbnailByPlacid()
  const renderSelectedItem = () => {
    return !selectedItem ? (
      <div
        className="flex md:flex-col relative w-[138px] h-[78px] justify-center outline outline-[4px] outline-main"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <button type="button" className="text-white font-bold z-40">
          썸네일 없음
        </button>
      </div>
    ) : (
      <div
        className={classNames(
          'flex w-[138px] relative outline outline-4 outline-main'
        )}
      >
        <img
          src={selectedItem?.path}
          className="w-[138px] h-[78px]"
          loading="lazy"
          alt=""
        />

        <div className="absolute w-[138px] h-[78px] flex">
          <span className="font-bold text-white text-[14px] ml-1 mt-1">
            선택됨
          </span>
        </div>
      </div>
    )
  }

  const handleCreateThumbnail = () => {
    if (placIdThumbnailRef.current) {
      placIdThumbnailRef.current.focus()
      placIdThumbnailRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
    const data = {
      thumbnailId: selectedItem?.id ?? 0,
      originImageUrl: selectedImgSrc ?? '',
      title1:
        watch('thumbnailInfo.title1') === ''
          ? ' '
          : watch('thumbnailInfo.title1'),
      title2:
        watch('thumbnailInfo.title2') === ''
          ? ' '
          : watch('thumbnailInfo.title2'),
      channel:
        watch('thumbnailInfo.channelName') === ''
          ? ' '
          : watch('thumbnailInfo.channelName'),
    }
    mutate(data, {
      onSuccess: (res) => {
        const newArr = [...placIdThumbnail]
        setPlacIdThumbnail(newArr)
        imgMutate(res, {
          onSuccess: (imgData) => {
            if (imgData.byteLength > 5700) {
              const base64 = btoa(
                new Uint8Array(imgData).reduce(
                  (data, byte) => data + String.fromCharCode(byte),
                  ''
                )
              )

              // 이미지 MIME 타입에 맞게 설정 (여기서는 JPEG로 가정)
              const imageUrl = `data:image/jpeg;base64,${base64}`
              setSelectedPlacIdThumbnail({ id: res, path: imageUrl })
              setValue('thumbnailHistoryId', res)
              newArr.push({ id: res, path: imageUrl })
            } else {
              setSelectedPlacIdThumbnail({ id: res, path: '' })
              newArr.push({ id: res, path: '' })
              setValue('thumbnailHistoryId', res)
            }
          },
          onError: (e: any) => {
            console.log(e?.response?.data)
          },
        })

        console.log('thumbnail 생성 성공:', res)
      },
      onError: (e: any) => {
        if (e?.response?.data?.errorCode === 'ERROR_THUMBNAIL_DAILY_LIMIT') {
          setIsThumbnailDailyLimit(true)
        }
      },
    })
  }

  const handleRefetchImgDataById = (id: number) => {
    imgMutate(id, {
      onSuccess: (imgData) => {
        if (imgData.byteLength > 5700) {
          const base64 = btoa(
            new Uint8Array(imgData).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ''
            )
          )

          // 이미지 MIME 타입에 맞게 설정 (여기서는 JPEG로 가정)
          const imageUrl = `data:image/jpeg;base64,${base64}`
          const updateArr = placIdThumbnail?.map((el: any) => {
            if (el.id === id) {
              setSelectedPlacIdThumbnail({ id, path: imageUrl })
              setValue('thumbnailHistoryId', id)
              return { id, path: imageUrl }
            }
            return el
          })
          setPlacIdThumbnail(updateArr)
        }
      },
      onError: (e) => {
        console.log(e)
      },
    })
  }

  const renderThumbnailListItem = (thumbnail: ThumbnailType, index: number) => {
    const isSelected = watch('thumbnailId') === thumbnail.id

    return (
      <div key={index} className="flex md:flex-col relative">
        {thumbnail?.name !== '없음' ? (
          <img
            src={thumbnail?.path}
            className="w-[138px] h-[78px]"
            loading="lazy"
            alt=""
          />
        ) : (
          <div
            className={classNames(
              'flex  md:flex-col  relative w-[138px] h-[78px] justify-center',
              {
                ' outline outline-[4px] outline-main':
                  thumbnail.name === '없음' &&
                  thumbnail.id === watch('thumbnailId'),
              }
            )}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
            onClick={() => {
              setSelectedItem(thumbnail)
              setValue('thumbnailId', thumbnail.id)
            }}
          >
            <button
              type="button"
              className={classNames(
                'text-white font-bold z-40 w-[138px] h-[78px]'
              )}
            >
              썸네일 없음
            </button>
          </div>
        )}
        {index === (isMobile ? 3 : 3) ? (
          <div
            className={`absolute w-[138px] h-[78px] flex items-center justify-center $ `}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
          >
            <button
              type="button"
              onClick={() => {
                setOpenModal((prev) => !prev)
              }}
              className=" text-white font-bold z-40"
            >
              {thumbnailList?.length - (isMobile ? 4 : 5)}개 더보기
            </button>
          </div>
        ) : (
          <div
            className={classNames('absolute w-[138px] h-[78px] flex', {
              'bg-opacity-50': isSelected,
            })}
            style={{
              backgroundColor: isSelected ? 'rgba(0, 0, 0, 0.5)' : undefined,
            }}
            onClick={() => {
              setSelectedItem(thumbnail)
              setValue('thumbnailId', thumbnail.id)
            }}
          >
            <div className="flex items-start h-full pt-1 mt-[6px] ml-[6px]">
              <PlanTag planName={translatePlanName(thumbnail.planName)} />
            </div>
            <Controller
              name="thumbnailId"
              control={control}
              render={({ field }: any) => (
                <label className="relative flex w-full cursor-pointer items-end justify-end mr-1 mb-1">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={isSelected}
                    disabled={!thumbnail.usable}
                    onChange={() => {
                      setValue('thumbnailId', thumbnail.id)
                    }}
                  />
                  <div
                    className={classNames(
                      'w-[20px] h-[20px] rounded-full border-2 flex items-center justify-center',
                      {
                        'bg-white border-main': isSelected,
                        'bg-white': !isSelected,
                      }
                    )}
                  >
                    {isSelected && (
                      <div className="w-3 h-3 bg-main rounded-full"></div>
                    )}
                  </div>
                </label>
              )}
            />
          </div>
        )}
      </div>
    )
  }

  const handleClickSaveBtn = () => {
    //1.썸네일을 저장했다는 state변경
    setIsSaveThumbnail(true)
    //2.썸네일을 AI방식으로 했는지 혹은 예전방식으로 했는지 저장
    thumbnailMethod === 'AI'
      ? setValue('thumbnailMethod', 'AI')
      : setValue('thumbnailMethod', 'DIRECT')
    //3.썸네일의 채널이름 적은 이름을 localstorage에 계속 저장하는지 확인해서
    //true면 로컬스토리지 저장 false면 로컬스토리지에서 삭제
    if (isRememberChannel) {
      saveLocalStorage('channel', watch('thumbnailInfo.channelName'))
    } else {
      removeLocalStorage('channel')
    }
    //4. 마지막으로 모달을 종료
    setOpenModal((prev) => !prev)
  }

  useEffect(() => {
    // 직접 링크로 생성하는 방식일때
    // 무조건 DIRECT 모달 띄우기
    if (isOpenLinkMethod || watch('thumbnailMethod') === 'DIRECT') {
      setThumbnailMethod('DIRECT')
      setAiName(false)
    } else if (!isOpenLinkMethod || watch('thumbnailMethod') === 'AI') {
      setThumbnailMethod('AI')
      setAiName(true)
    }
  }, [isOpenModal])

  return (
    <>
      <div className="  my-[40px]" ref={thumbNailDivRef}>
        <div className="flex items-center md:mb-[16px] gap-4 ">
          <p className="font-bold text-[18px]">썸네일 디자인 + 텍스트</p>
          <p className="flex  items-center gap-1 ">
            <span
              className="font-bold text-white text-[11px] px-1 py-[2px] rounded-[4px]"
              style={{
                background: `linear-gradient(to right,#6095FF,#00CBB3)`,
              }}
            >
              UPDATE
            </span>
            {!isMobile && (
              <span className="text-[14px] text-[#5E6068]">
                AI 추천으로 간편하게 썸네일을 생성하세요!
              </span>
            )}
          </p>
        </div>
        {isMobile && (
          <div className="mb-[16px] md:mb-0">
            <p className="text-[14px] text-[#5E6068]">
              AI 추천으로 간편하게 썸네일을 생성하세요!
            </p>
          </div>
        )}
        <div className="flex gap-4 md:items-center ">
          <div className="selected_video ">{renderSelectedItem()}</div>
          {/* 좋구알 비디오 리스트  */}
          {/* <div
            className={classNames('', {
              'flex md:flex-row flex-col gap-[24px] md:gap-1 justify-between bg-[#EEF1F7] md:h-[118px]  px-[10px] py-5 rounded-[10px]':
                !isMobile,
              'grid grid-cols-2 gap-x-[27px] gap-y-5 bg-[#EEF1F7]  px-[10px]  py-5 rounded-[10px] ':
                isMobile,
            })}
          >
            {thumbnailPlacIdList?.map((thumbnail: any, index: number) => {
              if (index <= (isMobile ? 3 : 3))
                return <>{renderThumbnailListItem(thumbnail, index)}</>

              return <> </>
            })}
          </div> */}
          <p className="w-auto md:h-[61px] rounded-[10px]  px-4 py-5 flex items-center justify-between font-bold">
            <button
              className="px-2 py-[6px] bg-[#2E343D] text-[white] rounded-[4px] text-[14px]"
              onClick={() => {
                setOpenModal((prev) => !prev)
              }}
              type="button"
            >
              선택 {'>'}
            </button>
          </p>
        </div>
      </div>
      <SelectionModal isModal={isOpenModal}>
        <div
          className={classNames('', {
            'bg-white w-[816px]  rounded-t-[20px] px-8 pt-8  flex flex-col   gap-4   shadow-main ':
              !isMobile,
            'bg-white  w-full h-full    flex flex-col   gap-8 pt-10 overflow-auto':
              isMobile,
          })}
        >
          {/* <div className="bg-white md:w-[816px] w-[335px] md:h-[670px] h-[630px] shadow-main rounded-[20px] md:p-8  p-4 flex flex-col overflow-auto  gap-4 "> */}
          <div
            className={classNames('flex flex-col  gap-4', {
              'px-4': isMobile,
            })}
          >
            <p className="flex md:justify-between justify-center items-center">
              <span className="font-bold text-[24px] text-left">
                썸네일 디자인 & 텍스트
              </span>
            </p>

            <div
              className={classNames('', {
                'flex items-center justify-between':
                  !isMobile && thumbnailMethod === 'AI',
                'flex items-center justify-end':
                  !isMobile && thumbnailMethod !== 'AI',
                'flex flex-col': isMobile,
              })}
            >
              {thumbnailMethod === 'AI' && (
                <p
                  className={classNames(
                    'inline-flex items-center justify-center gap-1 py-2 px-3 rounded-full text-white cursor-pointer',
                    {
                      'bg-black': !isAiName,
                      'bg-[#4360EC]': isAiName,
                      'w-[223px]': isMobile,
                    }
                  )}
                  onClick={() => {
                    setAiName((prev) => !prev)
                  }}
                >
                  <input
                    type="checkbox"
                    className={classNames('', {
                      'w-[18px] h-[18px] appearance-none border border-gray-400 rounded-md checked:bg-white checked:border-[#4360EC] cursor-pointer relative  after:content-[""] after:absolute after:w-[5px] after:h-[10px] after:border-r-2 after:border-b-2 after:border-[#4360EC] after:rotate-45 after:top-[2px] after:left-[5px] after:opacity-0 checked:after:opacity-100 mr-1':
                        isAiName,
                      '1 w-[18px] h-[18px] appearance-none border border-gray-400 rounded-md bg-white  cursor-pointer relative  before:content-[""] before:absolute before:w-[5px] before:h-[10px] before:border-r-2 before:border-b-2 before:border-[#D9DEE8] before:rotate-45 before:top-[2px] before:left-[5px]  before:opacity-100 mr-1':
                        !isAiName,
                    })}
                    checked={isAiName}
                  />
                  <StarIcon />
                  <span>AI 추천 데이터 반영하기</span>
                </p>
              )}
              <div
                className={classNames(
                  'inline-flex items-center md:justify-center justify-start'
                )}
              >
                <button
                  className="flex items-center justify-center gap-[8px]  py-[12.5px] rounded-[40px]"
                  type="button"
                  onClick={() => {
                    setOpen((prev) => !prev)
                    setThumbnailMethod((prev) => {
                      if (prev === 'AI') {
                        return 'DIRECT'
                      } else {
                        if (isOpenLinkMethod) {
                          return 'DIRECT'
                        }
                        return 'AI'
                      }
                    })
                  }}
                >
                  <span className="text-[14px] text-[#8F929B]">
                    직접 입력 생성 모드 열기
                  </span>
                  <img
                    src="assets/img/icon_up_arrow.png"
                    alt="arrow"
                    className={`w-[12.8px] h-[8.25px]  transition-transform duration-500 ${
                      isOpen ? '' : 'rotate-180'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
          {thumbnailMethod === 'AI' ? (
            <AiMethodThumbnail
              selectedLinks={selectedLinks}
              watch={watch}
              setValue={setValue}
              control={control}
              userInfo={userInfo}
              thumbnailList={thumbnailPlacIdList}
              translatePlanName={translatePlanName}
              aiRecommendThumbnailTitle={aiRecommendThumbnailTitle}
              error={error}
              setError={setError}
              isAiName={isAiName}
              isRememberChannel={isRememberChannel}
              setIsRememberChannel={setIsRememberChannel}
              placIdThumbnail={placIdThumbnail}
              setPlacIdThumbnail={setPlacIdThumbnail}
              setSelectedItem={setSelectedItem}
              isSaveThumbnail={isSaveThumbnail}
              setIsSaveThumbnail={setIsSaveThumbnail}
              selectedImgSrc={selectedImgSrc}
              setSelectedImgSrc={setSelectedImgSrc}
              selectedPlacIdThumbnail={selectedPlacIdThumbnail}
              setSelectedPlacIdThumbnail={setSelectedPlacIdThumbnail}
              handleCreateThumbnail={handleCreateThumbnail}
              imgIsPending={imgIsPending}
              createThumbnailPending={createThumbnailPending}
              handleRefetchImgDataById={handleRefetchImgDataById}
              placIdThumbnailRef={placIdThumbnailRef}
            />
          ) : (
            <>
              <DirectMethodThumbnail
                watch={watch}
                setValue={setValue}
                control={control}
                userInfo={userInfo}
                thumbnailList={thumbnailList}
                translatePlanName={translatePlanName}
                error={error}
                setError={setError}
                setOpenModal={setOpenModal}
                setSelectedItem={setSelectedItem}
                isSaveThumbnail={isSaveThumbnail}
                setIsSaveThumbnail={setIsSaveThumbnail}
                isRememberChannel={isRememberChannel}
                setIsRememberChannel={setIsRememberChannel}
                placIdThumbnail={placIdThumbnail}
              />
            </>
          )}
          {isMobile && thumbnailMethod === 'AI' && (
            <div className="relative px-8 pb-8  w-full md:w-[816px] bg-white rounded-b-[20px]   ">
              <button
                className={classNames(
                  'bg-main rounded-full w-full text-white font-bold h-[60px] disabled:opacity-30 ',
                  {}
                )}
                type="button"
                disabled={
                  thumbnailMethod === 'AI' && watch('thumbnailHistoryId') === 0
                }
                onClick={handleClickSaveBtn}
              >
                저장 후 닫기
              </button>
            </div>
          )}
        </div>
        {!isMobile && (
          <div
            className="sticky bottom-0 p-8 shadow-main w-full md:w-[816px] bg-white rounded-b-[20px] 
                    before:content-[''] before:absolute before:top-[-10px] before:left-0 
                    before:w-full before:h-[10px] before:bg-gradient-to-t before:from-black/10 before:to-transparent flex items-center justify-between z-50"
          >
            {thumbnailMethod === 'AI' && (
              <button
                className={classNames(
                  'h-[60px] rounded-full bg-[#2E343D] text-white font-bold flex justify-center items-center gap-1 disabled:opacity-30',
                  {
                    'w-[380px] ': !isMobile,
                    'w-[343px] ': isMobile,
                  }
                )}
                type="button"
                disabled={
                  placIdThumbnail.length === 3 ||
                  imgIsPending ||
                  createThumbnailPending
                }
                onClick={() => {
                  handleCreateThumbnail()
                }}
              >
                <StarIcon /> 썸네일 생성하기 {`${placIdThumbnail.length}/3`}{' '}
                <span className="w-[40px] bg-[#FD6767] h-[18px] rounded-[6px] text-[12px]">
                  할인중
                </span>
              </button>
            )}
            <button
              className={classNames(
                'bg-main rounded-full   text-white font-bold h-[60px]  ',
                {
                  'w-full': thumbnailMethod === 'DIRECT',
                  'w-[346px]': thumbnailMethod === 'AI',
                  ' opacity-30':
                    (thumbnailMethod === 'AI' &&
                      watch('thumbnailHistoryId') === 0) ||
                    (thumbnailMethod === 'DIRECT' &&
                      watch('thumbnailId') === 0),
                }
              )}
              disabled={
                (thumbnailMethod === 'AI' &&
                  watch('thumbnailHistoryId') === 0) ||
                (thumbnailMethod === 'DIRECT' && watch('thumbnailId') === 0)
              }
              type="button"
              onClick={handleClickSaveBtn}
            >
              저장 후 닫기
            </button>
          </div>
        )}
      </SelectionModal>
      <Modal isModal={isThumbnailDailyLimit}>
        <div className="h-screen w-full fixed left-0 top-0 flex flex-col justify-center items-center bg-black bg-opacity-50 text-center  z-50 ">
          <div className="bg-white w-[335px] h-auto rounded-t-[20px]  shadow-main   flex flex-col   gap-6 py-10 px-4 ">
            <p className="text-[18px] font-bold">크레딧 소진</p>
            <p className="text-[14px]">
              <span className="font-bold text-main">
                썸네일 AI 생성 크레딧을 모두 소진했습니다.{' '}
              </span>
              <br />
              직접 입력 생성 기능으로 썸네일을 생성하세요!
            </p>
          </div>
          <div className="w-[335px] text-[14px]  h-[60px]  flex justify-center items-center">
            <button
              className="text-white w-[100%] h-full bg-main rounded-b-[20px] border-b border-main font-bold"
              type="button"
              onClick={() => {
                setIsThumbnailDailyLimit(false)
              }}
            >
              확인
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
